import { qs, qsa, remove, add } from '@fluorescent/dom';

import { section } from '@/glow';
import { backgroundVideoHandler } from '@/lib/a11y';
import navigationDots from '@/lib/navigation-dots';

const selectors = {
  slider: '[data-slider]',
  slide: '.slideshow__cell',
  video: '.slideshow__video',
};

section('slideshow', {
  events: [],
  slideshow: null,
  dotNavigation: null,

  onLoad() {
    const slider = qs(selectors.slider, this.container);
    const slides = qsa(selectors.slide, this.container);
    const { autoplay } = slider.dataset;
    import('animejs/lib/anime.es.js') // load this before we need it 

    remove(slider, 'is-hidden');
    // trigger redraw for transition
    slider.offsetHeight;

    // Animate the first slide only when it is scrolled into view
    const observer = new IntersectionObserver(
      ([{ intersectionRatio: visible }]) => {
        if (visible) {
          this._animateSlide(slides[0]);
          this._unpause();
          observer.disconnect();
        }
      }
    );

    if (slides.length > 1) {
      import('flickity').then(({default: Flickity}) => {
        this.slideshow = new Flickity(slider, {
          adaptiveHeight: true,
          autoPlay: Number(autoplay),
          draggable: true,
          prevNextButtons: false,
          wrapAround: true,
          pageDots: false,
          dragThreshold: 5,
          pauseAutoPlayOnHover: !window.Shopify.designMode,
          on: {
            ready: () => {
              observer.observe(this.container);
            },
          },
        });

        // Pause player from playing while offscreen allowing text animation
        // when player intersects
        this._pause();

        this.dotNavigation = navigationDots(this.container, this.slideshow);

        this.slideshow.on('change', index => {
          const activeSlide = this.slideshow.cells[index].element;
          this._animateSlide(activeSlide);
          this.dotNavigation && this.dotNavigation.update(index);
        });

        slides.forEach(slide => {
          const videoWrapper = slide.querySelector(selectors.video);

          if (videoWrapper) {
            events.push(backgroundVideoHandler(slide));
          }
        });
      });
    } else if (slides.length) {
      add(slides[0], 'is-selected');
      this._animateSlide(slides[0]);
    }
  },

  _pause() {
    this.slideshow && this.slideshow.pausePlayer();
  },

  _unpause() {
    this.slideshow && this.slideshow.unpausePlayer();
  },

  _animateSlide(target) {
    import('animejs/lib/anime.es.js').then(({ default: anime }) => {
      anime({
        targets: qsa('.animate', target),
        easing: 'easeOutQuart',
        translateY: [
          { value: 40, duration: 0 },
          { value: 0, duration: 500 },
        ],
        opacity: [
          { value: 0, duration: 0 },
          { value: 1, duration: 500 },
        ],
        delay: anime.stagger(150, { start: 800 }),
      });
    });
  },

  onBlockSelect({ target }) {
    this.slideshow && this.slideshow.select(target.dataset.index);
    this._pause();
  },

  onBlockDeselect() {
    this._unpause();
  },

  onUnload() {
    this.slideshow && this.slideshow.destroy();
    this.events.forEach(unsubscribe => unsubscribe());
    this.dotNavigation && this.dotNavigation.unload();
  },
});
